import { FormattedMessage } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'universal-scripts'
import { clearSelection } from 'src/store/slices/query'
import {
  findNodeById,
  getSelectAlias,
  getSelectCol
} from 'src/utils/blueprintUtils'
import type {
  BlueprintNode,
  SelectNode,
  FilterNode,
  GroupNode,
  JoinNode,
  SortNode,
  PivotNode
} from 'src/types/queries'
import DataSetView from './DataSetView/DataSetView'

import './NodeViewer.css'

export default function NodeViewer({
  readOnly = false
}: {
  readOnly?: boolean
}) {
  const selectedNodeId = useAppSelector((s) => s.query.ui.selectedNodeId)
  const blueprint = useAppSelector((s) => s.query.draft.blueprint || [])

  if (!selectedNodeId) return null
  const found = findNodeById(blueprint, selectedNodeId)
  if (!found) return null

  if (found.isRowRoot && !readOnly) return null

  return <NodeViewerInner key={selectedNodeId} node={found.node} />
}

function NodeViewerInner({ node }: { node: BlueprintNode }) {
  const dispatch = useAppDispatch()

  return (
    <div id="node-viewer-panel" onClick={(e) => e.stopPropagation()}>
      <header className="node-editor-header">
        <h3>
          <FormattedMessage id="node-viewer.title" />
        </h3>
        <div className="node-edition-buttons">
          <span
            className="icon icon-cross standart-button"
            onClick={() => dispatch(clearSelection())}
          />
        </div>
      </header>
      <div className="node-viewer-body">
        {node.type === 'dataset' && <DataSetView node={node} />}
        {node.type === 'select' && <SelectView node={node} />}
        {node.type === 'filter' && <FilterView node={node} />}
        {node.type === 'group' && <GroupView node={node} />}
        {node.type === 'join' && <JoinView node={node} />}
        {node.type === 'sort' && <SortView node={node} />}
        {node.type === 'pivot' && <PivotView node={node} />}
      </div>
    </div>
  )
}

function SelectView({ node }: { node: SelectNode }) {
  const columns = node.columns.filter((c) => getSelectCol(c).trim() !== '')
  return (
    <div id="select-view" className="node-view">
      <h3>
        <FormattedMessage id={`operation-editor.option.${node.type}`} />
      </h3>
      <h4>
        <FormattedMessage id="node-viewer.select.columns" />
      </h4>
      <ul className="columns-list">
        {columns.map((c) => {
          const col = getSelectCol(c)
          const alias = getSelectAlias(c)
          return (
            <li key={col} className="column">
              {alias !== col ? `${col} → ${alias}` : col}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function FilterView({ node }: { node: FilterNode }) {
  return (
    <div id="filter-view" className="node-view">
      <h3>
        <FormattedMessage id={`operation-editor.option.${node.type}`} />
      </h3>
      {node.conditions.length > 1 && node.logic && (
        <p className="node-view-logic">
          <strong>
            <FormattedMessage id="filter-editor.logic.label" />
          </strong>
          <span>{node.logic}</span>
        </p>
      )}
      <ul className="conditions-list">
        <li className="condition condition-header">
          <div className="condition-separator">
            <h4>
              <FormattedMessage id="filter-editor.condition.col-title" />
            </h4>
          </div>
          <div className="condition-separator">
            <h4>
              <FormattedMessage id="filter-editor.condition.operator-title" />
            </h4>
          </div>
          <div className="condition-separator">
            <h4>
              <FormattedMessage id="filter-editor.condition.boundary-title" />
            </h4>
          </div>
        </li>
        {node.conditions.map((c, index) => {
          return (
            <li key={index} className="condition condition-body">
              <div className="condition-separator">{c.col}</div>
              <div className="condition-separator">{c.condition}</div>
              <div className="condition-separator">
                {String(c.boundary ?? '')}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function GroupView({ node }: { node: GroupNode }) {
  return (
    <div id="group-view" className="node-view">
      <h3>
        <FormattedMessage id={`operation-editor.option.${node.type}`} />
      </h3>
      <h4>
        <FormattedMessage id="node-viewer.group.columns" />
      </h4>
      <ul className="columns-list">
        {node.columns.map((c, i) => (
          <li key={i}>
            <span className="fn-selected">
              {c.fn ? (
                `${c.fn}`
              ) : (
                <FormattedMessage id="group-editor.fn.groupby" />
              )}
            </span>
            <span className="column">{c.col}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function JoinView({ node }: { node: JoinNode }) {
  return (
    <div id="filter-view" className="node-view">
      <h3>
        <FormattedMessage id={`operation-editor.option.${node.type}`} />
      </h3>
      <p className="node-view-logic">
        <strong>
          <FormattedMessage id="join-editor.type.label" />
        </strong>
        <span>{node.joinType}</span>
      </p>

      <div className="columns-title">
        <h4>
          <FormattedMessage id="join-editor.left-source.label" />
        </h4>
        <h4>
          <FormattedMessage id="join-editor.right-source.label" />
        </h4>
      </div>
      <ul className="columns-list">
        {node.on.map((pair, i) => (
          <li key={i}>
            <span className="column">{pair.left}</span>
            <span className="column">{pair.right}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SortView({ node }: { node: SortNode }) {
  return (
    <div className="node-view">
      <h3>
        <FormattedMessage id={`operation-editor.option.${node.type}`} />
      </h3>
      <div className="columns-title">
        <h4>
          <FormattedMessage id="sort-editor.column.label" />
        </h4>
        <h4>
          <FormattedMessage id="sort-editor.direction.label" />
        </h4>
      </div>
      <ul className="columns-list">
        {node.cols.map((c, i) => (
          <li key={i}>
            <span className="column">{c.col}</span>
            <span className="sort-direction">
              <FormattedMessage id={`sort-editor.direction.${c.direction}`} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function PivotView({ node }: { node: PivotNode }) {
  return (
    <div id="pivot-view" className="node-view">
      <h3>
        <FormattedMessage id={`operation-editor.option.${node.type}`} />
      </h3>
      <div className="columns-title">
        <h4>
          <FormattedMessage id="node-viewer.pivot.pivot-column" />
        </h4>
        <h4>
          <FormattedMessage id="node-viewer.pivot.value-column" />
        </h4>
      </div>
      <ul className="columns-pivot">
        <li className="ellipsis">{node.pivotColumn}</li>
        <li className="ellipsis">{node.valueColumn}</li>
      </ul>

      <h4>
        <FormattedMessage id="node-viewer.pivot.values" />
      </h4>
      <ul className="value-chips">
        {node.pivotValues.map((v) => (
          <li key={String(v)} className="chip">
            <span>{String(v)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
